import axios from "axios";
import { endpoints } from "../config/endpoints";

// API endpoint for thread management
const THREAD_MANAGEMENT_API = endpoints.api.threadManagement;

// Add allowed origins to ensure CORS compliance
const headers = {
  "Content-Type": "application/json",
  Origin: window.location.origin,
};

/**
 * Lists all threads for a specific user
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} - Array of thread objects containing bot_id and thread_id
 */
export const listUserThreads = async (userId) => {
  if (!userId) {
    throw new Error("User ID is required");
  }

  try {
    console.log('🔍 DEBUG: listUserThreads called with userId:', userId);
    console.log('🔍 DEBUG: THREAD_MANAGEMENT_API:', THREAD_MANAGEMENT_API);
    
    // Set user_id cookie for middleware authentication (like ChatBox does)
    document.cookie = `user_id=${userId}; path=/; SameSite=None; Secure`;
    console.log('🔍 DEBUG: Set user_id cookie:', userId);
    
    const payload = {
      action: "list_threads",
      user_id: userId,
    };
    console.log('🔍 DEBUG: Request payload:', payload);
    
    const response = await axios.post(
      THREAD_MANAGEMENT_API,
      payload,
      {
        headers,
      }
    );

    console.log('🔍 DEBUG: API Response status:', response.status);
    console.log('🔍 DEBUG: API Response data:', response.data);
    
    const threads = response.data.threads || [];
    console.log('🔍 DEBUG: Extracted threads:', threads);
    
    return threads;
  } catch (error) {
    console.error("🔍 DEBUG: Error listing threads:", error);
    if (error.response) {
      console.error("🔍 DEBUG: Error response data:", error.response.data);
      console.error("🔍 DEBUG: Error response status:", error.response.status);
    }
    return [];
  }
};

/**
 * Creates a new thread for a user-bot combination
 * @param {string} userId - The user's ID
 * @param {string|number} botId - The bot's ID
 * @returns {Promise<Object>} - Object containing thread_id, created flag, and message
 */
export const createThread = async (userId, botId) => {
  if (!userId || botId === undefined) {
    throw new Error("User ID and Bot ID are required");
  }

  try {
    // Set user_id cookie for middleware authentication (like ChatBox does)
    document.cookie = `user_id=${userId}; path=/; SameSite=None; Secure`;
    
    // Ensure proper request format
    const payload = {
      action: "create_thread",
      user_id: userId,
      bot_id: botId,
    };

    console.log("Creating thread with payload:", payload);
    console.log("Thread management API URL:", THREAD_MANAGEMENT_API);
    console.log("Request headers:", headers);

    const response = await axios.post(THREAD_MANAGEMENT_API, payload, {
      headers,
    });

    console.log("Thread creation response:", response.data);
    console.log("Response status:", response.status);

    if (!response.data.thread_id) {
      console.error(
        "Thread creation failed: No thread_id in response",
        response.data
      );
      throw new Error("Thread creation failed: No thread_id in response");
    }

    console.log(
      `Successfully created thread with ID: ${response.data.thread_id}`
    );

    // Verify the thread_id is a valid string
    if (
      typeof response.data.thread_id !== "string" ||
      response.data.thread_id.trim() === ""
    ) {
      console.error(
        "Thread creation returned invalid thread_id",
        response.data.thread_id
      );
      throw new Error("Thread creation returned invalid thread_id");
    }

    return {
      thread_id: response.data.thread_id,
      created: response.data.created,
      message: response.data.message,
    };
  } catch (error) {
    console.error("Error creating thread:", error);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
    }
    throw error;
  }
};

/**
 * Deletes a thread from the database
 * @param {string} threadId - The ID of the thread to delete
 * @returns {Promise<Object>} - Object containing deleted flag and message
 */
export const deleteThread = async (threadId) => {
  if (!threadId) {
    throw new Error("Thread ID is required");
  }

  try {
    // Set user_id cookie from current user session if available
    // Note: For delete operations, we might need to get the current user
    // This is a limitation of not passing userId to delete function
    const currentCookie = document.cookie;
    console.log("Current cookies for delete:", currentCookie);
    
    // Ensure proper request format
    const payload = {
      action: "delete_thread",
      thread_id: threadId,
    };

    console.log("Deleting thread with payload:", payload);

    const response = await axios.post(THREAD_MANAGEMENT_API, payload, {
      headers,
    });

    console.log("Thread deletion response:", response.data);

    return {
      deleted: response.data.deleted,
      message: response.data.message,
    };
  } catch (error) {
    console.error("Error deleting thread:", error);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
    }
    throw error;
  }
};
